import PocketBase from 'pocketbase'
import type { PackingList, Category, Item } from '@/types'

const pb = new PocketBase('/packliste')

export { pb }

export class PocketBaseService {
  // Packing Lists
  async createPackingList(data: Partial<PackingList>) {
    return await pb.collection('packing_lists').create(data)
  }

  async getPackingList(id: string): Promise<PackingList> {
    return await pb.collection('packing_lists').getOne(id)
  }

  async updatePackingList(id: string, data: Partial<PackingList>) {
    return await pb.collection('packing_lists').update(id, data)
  }

  async getAllPackingLists(): Promise<PackingList[]> {
    return await pb.collection('packing_lists').getFullList({
      sort: '-updated'
    })
  }

  async deletePackingList(id: string) {
    return await pb.collection('packing_lists').delete(id)
  }

  // Categories
  async createCategory(data: Partial<Category>) {
    return await pb.collection('categories').create(data)
  }

  async getCategoriesByPackingList(packingListId: string): Promise<Category[]> {
    return await pb.collection('categories').getFullList({
      filter: `packing_list = "${packingListId}"`,
      sort: 'order'
    })
  }

  async updateCategory(id: string, data: Partial<Category>) {
    return await pb.collection('categories').update(id, data)
  }

  async deleteCategory(id: string) {
    return await pb.collection('categories').delete(id)
  }

  // Items
  async createItem(data: Partial<Item>) {
    return await pb.collection('items').create(data)
  }

  async getItemsByCategory(categoryId: string): Promise<Item[]> {
    return await pb.collection('items').getFullList({
      filter: `category = "${categoryId}"`,
      sort: 'order'
    })
  }

  async updateItem(id: string, data: Partial<Item>) {
    return await pb.collection('items').update(id, data)
  }

  async deleteItem(id: string) {
    return await pb.collection('items').delete(id)
  }

  // Get all data for a packing list
  async getPackingListWithData(id: string) {
    const packingList = await this.getPackingList(id)
    const categories = await this.getCategoriesByPackingList(id)
    
    // Process categories sequentially to avoid auto-cancellation
    const categoriesWithItems = []
    for (const category of categories) {
      const items = await this.getItemsByCategory(category.id)
      categoriesWithItems.push({
        ...category,
        items
      })
      // Small delay between requests to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 50))
    }

    return {
      packingList,
      categories: categoriesWithItems
    }
  }
}

export const pocketbaseService = new PocketBaseService()